import type { ProjectTask } from '@/types/odoo';
import { updateTaskDescription, uploadTaskDescriptionImage } from '@/api/tasks';
import { useProjectViewStore } from '@/stores/projectView';
import { FaseBestanden } from './FaseBestanden';
import { RichDescriptionEditor } from './RichDescriptionEditor';

interface TaskDetailPanelProps {
  projectId: number;
  /** Active fase — fase-bestanden are FASE-scoped, shown in this task panel. */
  faseId: number | null;
  task: ProjectTask;
  installerNames: Record<string, string>;
  /** Show the description and fase files without any way to change them. */
  readOnly?: boolean;
}

/** yyyy-mm-dd → dd/mm/yyyy */
function fmtDate(deadline: string | false): string {
  if (!deadline) return '—';
  const [y, m, d] = String(deadline).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

const NL_LABELS = {
  label: 'Beschrijving',
  placeholder: 'Notities voor deze taak…',
  unsaved: 'Niet opgeslagen',
  saving: 'Opslaan…',
  saved: 'Opgeslagen',
  error: 'Opslaan mislukt',
  retry: 'Opnieuw proberen',
};

const NL_LABELS_READ_ONLY = { ...NL_LABELS, placeholder: 'Geen beschrijving' };

/**
 * Inline detail panel for a selected planning task (Fase 3a): meta + a
 * rich HTML description editor (saved on blur, with image paste/drop).
 * Fase-bestanden are added in Fase 3b.
 */
export function TaskDetailPanel({ projectId, faseId, task, installerNames, readOnly = false }: TaskDetailPanelProps) {

  // Read-only: the editor is disabled, and these refuse on their own as well so
  // no path through the editor can reach the API.
  const handleSaveHtml = async (html: string): Promise<string> => {
    if (readOnly) throw new Error('Alleen lezen');
    const result = await updateTaskDescription(projectId, task.id, html);
    const savedHtml = result.description;
    // Sync the store cache so switch-away/back shows saved text.
    useProjectViewStore.getState().applyTaskDescription(projectId, task.id, savedHtml);
    return savedHtml;
  };

  const handleUploadImage = async (file: File) => {
    if (readOnly) throw new Error('Alleen lezen');
    return uploadTaskDescriptionImage(projectId, task.id, file);
  };

  const installers =
    (Array.isArray(task.x_studio_installateur) ? task.x_studio_installateur : [])
      .map((id) => installerNames[String(id)] ?? `#${id}`)
      .join(', ') || '—';

  return (
    <section aria-label={`Taak ${task.name}`} className="rounded-xl border-2 border-td-blue/40 bg-white p-4 shadow-sm">
      <h3 className="text-base font-extrabold text-td-navy break-words">{task.name}</h3>

      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Meta label="Datum" value={fmtDate(task.date_deadline)} />
        <Meta label="Installateur" value={installers} />
        <Meta label="Verdieping" value={task.x_studio_verdieping || '—'} />
        <Meta label="Status" value={task.x_studio_planningstage || '—'} />
      </div>

      <div className="mt-4">
        <RichDescriptionEditor
          taskKey={task.id}
          valueHtml={task.description || ''}
          onSaveHtml={handleSaveHtml}
          uploadImage={handleUploadImage}
          labels={readOnly ? NL_LABELS_READ_ONLY : NL_LABELS}
          disabled={readOnly}
          id={`task-desc-${task.id}`}
        />
      </div>

      {faseId !== null && <FaseBestanden projectId={projectId} faseId={faseId} readOnly={readOnly} />}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{label}</p>
      <p className="mt-0.5 truncate text-sm font-semibold text-td-navy" title={value}>{value}</p>
    </div>
  );
}
